import { useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import AdminLayout from "../components/AdminLayout";
import {
  AdminTable, AdminModal, ConfirmDialog, FormField,
  LoadingState, ErrorState, ApiModeBanner, inputCls,
} from "../components/AdminUI";
import { useAdminT } from "../../../hooks/useAdminT";
import { useToast } from "../../../components/base/Toast";
import { useAdminData } from "../../../store/adminDataStore";
import type { BookingCostItem, CostType, CostStatus } from "../../../store/types";

const COST_TYPES: CostType[] = ["flight", "train", "accommodation", "local_transport", "ticket", "other"];
const COST_STATUSES: CostStatus[] = ["estimated", "booked", "paid", "non_refundable"];

const COST_TYPE_ICONS: Record<CostType, string> = {
  flight: "ri-plane-line",
  train: "ri-train-line",
  accommodation: "ri-hotel-line",
  local_transport: "ri-taxi-line",
  ticket: "ri-ticket-line",
  other: "ri-more-line",
};

const STATUS_STYLES: Record<CostStatus, string> = {
  estimated: "bg-amber-50 text-amber-600",
  booked: "bg-[#C8B8A6]/20 text-[#C8B8A6]",
  paid: "bg-emerald-50 text-emerald-600",
  non_refundable: "bg-red-50 text-red-500",
};

const NOW = () => new Date().toISOString();

function emptyItem(orderId = ""): BookingCostItem {
  return {
    id: `bci-${Date.now()}`,
    order_id: orderId,
    cost_type: "flight",
    amount_twd: 0,
    currency: "TWD",
    cost_status: "estimated",
    description: "",
    created_at: NOW(),
    updated_at: NOW(),
  };
}

function EditModal({
  form, onChange, onClose, onSave, saving, isNew, orders,
}: {
  form: BookingCostItem; onChange: (v: BookingCostItem) => void;
  onClose: () => void; onSave: () => void; saving: boolean; isNew: boolean;
  orders: Array<{ id: string; label: string }>;
}) {
  const t = useAdminT();
  const set = (field: keyof BookingCostItem, value: unknown) => onChange({ ...form, [field]: value });
  return (
    <AdminModal
      title={isNew ? t("tabifotoAdmin.booking_cost_items.add_item") : t("tabifotoAdmin.booking_cost_items.edit_item")}
      onClose={onClose} onSave={onSave} saving={saving}
    >
      <FormField label={t("tabifotoAdmin.booking_cost_items.order_id")} required>
        <select className={inputCls} value={form.order_id} onChange={(e) => set("order_id", e.target.value)}>
          <option value="">— {t("tabifotoAdmin.booking_cost_items.order_id")} —</option>
          {orders.map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
        </select>
      </FormField>
      <div className="grid grid-cols-2 gap-4">
        <FormField label={t("tabifotoAdmin.booking_cost_items.cost_type")} required>
          <select className={inputCls} value={form.cost_type} onChange={(e) => set("cost_type", e.target.value as CostType)}>
            {COST_TYPES.map((c) => (
              <option key={c} value={c}>{t(`tabifotoAdmin.booking_cost_items.cost_type_${c}`)}</option>
            ))}
          </select>
        </FormField>
        <FormField label={t("tabifotoAdmin.booking_cost_items.cost_status")} required>
          <select className={inputCls} value={form.cost_status} onChange={(e) => set("cost_status", e.target.value as CostStatus)}>
            {COST_STATUSES.map((s) => (
              <option key={s} value={s}>{t(`tabifotoAdmin.booking_cost_items.cost_status_${s}`)}</option>
            ))}
          </select>
        </FormField>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <FormField label={t("tabifotoAdmin.booking_cost_items.amount_twd")} required>
          <input className={inputCls} type="number" min={0} value={form.amount_twd}
            onChange={(e) => set("amount_twd", Number(e.target.value))} />
        </FormField>
        <FormField label={t("tabifotoAdmin.booking_cost_items.currency")}>
          <input className={inputCls} value={form.currency}
            onChange={(e) => set("currency", e.target.value)} placeholder="TWD" />
        </FormField>
      </div>
      <FormField label={t("tabifotoAdmin.booking_cost_items.description")}>
        <textarea className={`${inputCls} h-20 resize-none`} value={form.description}
          onChange={(e) => set("description", e.target.value)} maxLength={500} />
      </FormField>
      {form.cost_status === "non_refundable" && (
        <div className="flex items-start gap-2 p-3 bg-red-50 rounded-md border border-red-100 mt-1">
          <div className="w-4 h-4 flex items-center justify-center text-red-400 shrink-0 mt-0.5">
            <i className="ri-alert-line text-sm" />
          </div>
          <p className="text-xs text-red-500 font-light">{t("tabifotoAdmin.booking_cost_items.non_refundable_warning")}</p>
        </div>
      )}
    </AdminModal>
  );
}

export default function AdminBookingCostItemsPage() {
  const t = useAdminT();
  const { state, status, isApiMode, upsertCostItem, removeCostItem, refetchCostItems } = useAdminData();
  const toast = useToast();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const preFilterOrder = searchParams.get("order") ?? "all";

  const [editItem, setEditItem] = useState<BookingCostItem | null>(null);
  const [saving, setSaving] = useState(false);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [filterOrder, setFilterOrder] = useState<string>(preFilterOrder);
  const [filterStatus, setFilterStatus] = useState<CostStatus | "all">("all");

  const orders = state.orders.map((o) => ({ id: o.id, label: `${o.order_number} — ${o.customer_name}` }));

  const filtered = [...state.bookingCostItems]
    .filter((c) => {
      if (filterOrder !== "all" && c.order_id !== filterOrder) return false;
      if (filterStatus !== "all" && c.cost_status !== filterStatus) return false;
      return true;
    })
    .sort((a, b) => b.created_at.localeCompare(a.created_at));

  const nonRefundableTotal = filtered
    .filter((c) => c.cost_status === "non_refundable")
    .reduce((sum, c) => sum + c.amount_twd, 0);

  const handleSave = async () => {
    if (!editItem) return;
    if (!editItem.order_id) { toast.error(t("tabifotoAdmin.common.save_error")); return; }
    setSaving(true);
    try {
      await upsertCostItem(editItem);
      toast.success(t("tabifotoAdmin.common.save_success"));
      setEditItem(null);
    } catch { toast.error(t("tabifotoAdmin.common.save_error")); }
    finally { setSaving(false); }
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    try {
      await removeCostItem(deleteId);
      toast.success(t("tabifotoAdmin.common.delete_success"));
    } catch { toast.error(t("tabifotoAdmin.common.delete_error")); }
    finally { setDeleteId(null); }
  };

  const STATUS_FILTER_TABS = [
    { key: "all" as const, label: t("tabifotoAdmin.booking_cost_items.filter_all") },
    ...COST_STATUSES.map((s) => ({ key: s, label: t(`tabifotoAdmin.booking_cost_items.cost_status_${s}`) })),
  ];

  const headers = [
    t("tabifotoAdmin.booking_cost_items.col_order"),
    t("tabifotoAdmin.booking_cost_items.col_cost_type"),
    t("tabifotoAdmin.booking_cost_items.col_amount"),
    t("tabifotoAdmin.booking_cost_items.col_cost_status"),
    t("tabifotoAdmin.booking_cost_items.col_description"),
    t("tabifotoAdmin.booking_cost_items.col_created_at"),
    t("tabifotoAdmin.booking_cost_items.col_actions"),
  ];

  return (
    <AdminLayout
      title={t("tabifotoAdmin.booking_cost_items.title")}
      subtitle={t("tabifotoAdmin.booking_cost_items.subtitle")}
      action={
        <button onClick={() => setEditItem(emptyItem(filterOrder !== "all" ? filterOrder : ""))}
          className="flex items-center gap-2 px-4 py-2 bg-[#2C2C2C] text-white text-sm rounded-md hover:bg-[#2C2C2C]/80 transition-colors cursor-pointer whitespace-nowrap">
          <div className="w-4 h-4 flex items-center justify-center"><i className="ri-add-line text-base" /></div>
          {t("tabifotoAdmin.booking_cost_items.add_item")}
        </button>
      }
    >
      <ApiModeBanner isApiMode={isApiMode} />

      <div className="flex items-center gap-3 mb-4 flex-wrap">
        <div className="flex gap-1 bg-[#F7F6F3] p-1 rounded-lg">
          <button onClick={() => setFilterOrder("all")}
            className={`px-3 py-1.5 rounded-md text-xs font-light transition-colors cursor-pointer whitespace-nowrap ${filterOrder === "all" ? "bg-white text-[#2C2C2C]" : "text-[#2C2C2C]/40 hover:text-[#2C2C2C]/70"}`}>
            {t("tabifotoAdmin.booking_cost_items.filter_all")}
          </button>
          {orders.map((o) => (
            <button key={o.id} onClick={() => setFilterOrder(o.id)}
              className={`px-3 py-1.5 rounded-md text-xs font-light transition-colors cursor-pointer whitespace-nowrap ${filterOrder === o.id ? "bg-white text-[#2C2C2C]" : "text-[#2C2C2C]/40 hover:text-[#2C2C2C]/70"}`}>
              {o.label.split(" — ")[0]}
            </button>
          ))}
        </div>
        <div className="flex gap-1 bg-[#F7F6F3] p-1 rounded-lg">
          {STATUS_FILTER_TABS.map((tab) => (
            <button key={tab.key} onClick={() => setFilterStatus(tab.key)}
              className={`px-3 py-1.5 rounded-md text-xs font-light transition-colors cursor-pointer whitespace-nowrap ${filterStatus === tab.key ? "bg-white text-[#2C2C2C]" : "text-[#2C2C2C]/40 hover:text-[#2C2C2C]/70"}`}>
              {tab.label}
            </button>
          ))}
        </div>
        {nonRefundableTotal > 0 && (
          <div className="flex items-center gap-1.5 px-3 py-1.5 bg-red-50 rounded-lg border border-red-100">
            <div className="w-3.5 h-3.5 flex items-center justify-center text-red-400"><i className="ri-alert-line text-xs" /></div>
            <span className="text-xs text-red-500 font-light">
              {t("tabifotoAdmin.booking_cost_items.cost_status_non_refundable")}: NT${nonRefundableTotal.toLocaleString()}
            </span>
          </div>
        )}
      </div>

      {status.costItems.loading ? <LoadingState /> :
        status.costItems.error ? <ErrorState message={status.costItems.error} onRetry={refetchCostItems} /> : (
          <AdminTable headers={headers} empty={filtered.length === 0} emptyMessage={t("tabifotoAdmin.booking_cost_items.no_items")}>
            {filtered.map((item) => {
              const order = state.orders.find((o) => o.id === item.order_id);
              return (
                <tr key={item.id} className={`border-b border-[#D9D9D9]/40 hover:bg-[#F7F6F3]/60 transition-colors ${item.cost_status === "non_refundable" ? "bg-red-50/30" : ""}`}>
                  <td className="px-5 py-3">
                    <button onClick={() => navigate(`/admin/orders?order=${item.order_id}`)}
                      className="text-xs font-mono text-[#C8B8A6] hover:text-[#2C2C2C] cursor-pointer whitespace-nowrap">
                      {order ? order.order_number : item.order_id}
                    </button>
                  </td>
                  <td className="px-5 py-3">
                    <div className="flex items-center gap-1.5">
                      <div className="w-4 h-4 flex items-center justify-center text-[#2C2C2C]/40">
                        <i className={`${COST_TYPE_ICONS[item.cost_type]} text-sm`} />
                      </div>
                      <span className="text-xs text-[#2C2C2C]/70">{t(`tabifotoAdmin.booking_cost_items.cost_type_${item.cost_type}`)}</span>
                    </div>
                  </td>
                  <td className="px-5 py-3">
                    <span className={`text-sm font-light ${item.cost_status === "non_refundable" ? "text-red-500 font-normal" : "text-[#2C2C2C]"}`}>
                      NT${item.amount_twd.toLocaleString()}
                    </span>
                    <span className="text-xs text-[#2C2C2C]/30 ml-1">{item.currency}</span>
                  </td>
                  <td className="px-5 py-3">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-light ${STATUS_STYLES[item.cost_status]}`}>
                      {t(`tabifotoAdmin.booking_cost_items.cost_status_${item.cost_status}`)}
                    </span>
                  </td>
                  <td className="px-5 py-3 max-w-xs">
                    <p className="text-xs text-[#2C2C2C]/60 truncate font-light">{item.description || "—"}</p>
                  </td>
                  <td className="px-5 py-3 text-xs text-[#2C2C2C]/40">{item.created_at.slice(0, 10)}</td>
                  <td className="px-5 py-3">
                    <div className="flex items-center gap-2">
                      <button onClick={() => setEditItem({ ...item })}
                        className="w-7 h-7 flex items-center justify-center rounded-md text-[#2C2C2C]/40 hover:text-[#2C2C2C] hover:bg-[#F7F6F3] transition-colors cursor-pointer">
                        <i className="ri-edit-line text-base" />
                      </button>
                      <button onClick={() => setDeleteId(item.id)}
                        className="w-7 h-7 flex items-center justify-center rounded-md text-[#2C2C2C]/40 hover:text-red-500 hover:bg-red-50 transition-colors cursor-pointer">
                        <i className="ri-delete-bin-line text-base" />
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </AdminTable>
        )}

      {editItem && (
        <EditModal form={editItem} onChange={setEditItem} onClose={() => setEditItem(null)}
          onSave={handleSave} saving={saving}
          isNew={!state.bookingCostItems.some((c) => c.id === editItem.id)}
          orders={orders} />
      )}
      {deleteId && (
        <ConfirmDialog title={t("tabifotoAdmin.booking_cost_items.delete_confirm")}
          message={t("tabifotoAdmin.common.cannot_undone")} danger
          onConfirm={handleDelete} onCancel={() => setDeleteId(null)} />
      )}
    </AdminLayout>
  );
}
